import { useEffect, useCallback, useState } from 'react';

import { getHelpText, HelpEntry } from '../constants/helpTexts';
import { useHelpModeContext } from '../components/help-mode/HelpModeContext';

interface UseHelpModeResult {
  isHelpMode: boolean;
  helpData: HelpEntry | null;
  closeHelp: () => void;
}

export const useHelpMode = (): UseHelpModeResult => {
  const { isHelpMode, toggleHelpMode, disableHelpMode } = useHelpModeContext();
  const [helpData, setHelpData] = useState<HelpEntry | null>(null);

  // Горячие клавиши: Shift+H (включение/выключение), Escape (выключение)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+Shift+H — переключение режима помощи (H и русская Р)
      if (e.shiftKey && e.ctrlKey && !e.altKey && !e.metaKey &&
          (e.key === 'H' || e.key === 'h' || e.key === 'Р' || e.key === 'р')) {
        e.preventDefault();
        e.stopPropagation();
        toggleHelpMode();
        return;
      }

      // Escape — выключение режима помощи
      if (e.key === 'Escape' && isHelpMode) {
        e.preventDefault();
        disableHelpMode();
        setHelpData(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [isHelpMode, toggleHelpMode, disableHelpMode]);

  // Перехват кликов в режиме помощи
  useEffect(() => {
    if (!isHelpMode) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // Не перехватываем клики внутри модалки справки и маски
      if (target.closest('.help-mode-modal') || target.closest('.ant-modal-mask')) {
        return;
      }

      // Shift+клик — пропускаем как обычный клик для навигации
      if (e.shiftKey) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      // Ищем ближайший элемент с data-help-id
      const helpElement = target.closest('[data-help-id]') as HTMLElement | null;

      if (helpElement) {
        const helpId = helpElement.getAttribute('data-help-id');
        if (helpId) {
          const entry = getHelpText(helpId);
          if (entry) {
            setHelpData(entry);
            return;
          }
        }
      }

      // Fallback: элемент без подсказки
      setHelpData({
        title: 'Справка недоступна',
        description:
          'Для этого элемента пока нет описания. Если вам нужна помощь — обратитесь в поддержку.',
      });
    };

    document.addEventListener('click', handleClick, { capture: true });
    return () => {
      document.removeEventListener('click', handleClick, { capture: true });
    };
  }, [isHelpMode]);

  const closeHelp = useCallback(() => {
    setHelpData(null);
  }, []);

  return { isHelpMode, helpData, closeHelp };
};
